/**
 * A quiet status line shown while the assistant works, in the manner of the
 * Claude Code CLI: one small glyph that breathes, a craft verb that changes
 * every few seconds, and the elapsed time. Quiet on purpose: grey text, no
 * motion beyond the glyph. With "reduce motion" set in the OS the glyph
 * stands still.
 */
import { Box, Stack, Typography } from "@mui/material";
import { useTranslate } from "@tolgee/react";
import React from "react";

const GLYPHS = ["·", "✢", "✳", "✶", "✻", "✽", "✻", "✶", "✳", "✢"] as const;
const STILL_GLYPH = "✻";
const GLYPH_INTERVAL_MS = 130;
const VERB_INTERVAL_MS = 4000;

/** The craft verbs the status line rotates through (Tolgee keys, ns "Assistant"). */
export const WORKING_VERB_KEYS = [
  "Tinkering…",
  "Measuring…",
  "Screwing…",
  "Calculating…",
  "Sorting material…",
  "Checking the plans…",
  "Sealing…",
  "Laying out…",
] as const;

/**
 * The glyph shown `elapsedMs` into the wait: cycles every `GLYPH_INTERVAL_MS`,
 * or stands still on `STILL_GLYPH` when reduced motion is preferred. Pure,
 * so it is unit-testable without real timers.
 */
export function workingGlyphAt(
  elapsedMs: number,
  reducedMotion: boolean
): string {
  if (reducedMotion) return STILL_GLYPH;
  return GLYPHS[Math.floor(elapsedMs / GLYPH_INTERVAL_MS) % GLYPHS.length];
}

/**
 * The verb (Tolgee key) shown `elapsedMs` into the wait: rotates every
 * `VERB_INTERVAL_MS`. Pure, so it is unit-testable without real timers.
 */
export function workingVerbKeyAt(
  elapsedMs: number
): (typeof WORKING_VERB_KEYS)[number] {
  return WORKING_VERB_KEYS[
    Math.floor(elapsedMs / VERB_INTERVAL_MS) % WORKING_VERB_KEYS.length
  ];
}

const prefersReducedMotion = () =>
  typeof window.matchMedia === "function" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export function AgentWorkingStatus() {
  const { t } = useTranslate(["Assistant"]);
  const [startedAt] = React.useState(() => Date.now());
  const [now, setNow] = React.useState(startedAt);
  const still = React.useMemo(prefersReducedMotion, []);

  React.useEffect(() => {
    const timer = window.setInterval(
      () => setNow(Date.now()),
      still ? 1000 : GLYPH_INTERVAL_MS
    );
    return () => window.clearInterval(timer);
  }, [still]);

  const elapsed = now - startedAt;

  return (
    <Stack
      direction="row"
      spacing={1}
      role="status"
      aria-label={t("Working…")}
      sx={{ alignItems: "baseline", color: "text.secondary" }}
    >
      <Box
        component="span"
        aria-hidden
        sx={{
          color: "primary.main",
          width: "1em",
          textAlign: "center",
          fontSize: "1rem",
          lineHeight: 1,
        }}
      >
        {workingGlyphAt(elapsed, still)}
      </Box>
      <Typography variant="body2" color="inherit">
        {t(workingVerbKeyAt(elapsed))}
      </Typography>
      <Typography variant="caption" color="text.disabled">
        {t("{seconds} s", {
          ns: "Assistant",
          seconds: Math.floor(elapsed / 1000),
        })}
      </Typography>
    </Stack>
  );
}
